import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { VisuallyHidden } from './ui/visually-hidden';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Mail, Lock, User, Chrome, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import PasswordChecklist, { isPasswordStrong } from './PasswordChecklist';
import { track, identifyUser } from '../utils/analytics';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'login' | 'register';
  resetToken?: string | null;
}

export default function AuthModal({ open, onOpenChange, defaultTab = 'login', resetToken = null }: AuthModalProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(!!resetToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const { login, register, loginWithGoogle, setError } = useAuth();

  const API_URL = '';
  
  // Handle reset token from URL
  useEffect(() => {
    if (resetToken) {
      setShowResetPassword(true);
    }
  }, [resetToken]);

  // Fire signup_modal_opened once per open. We dedupe with `open` going
  // false→true so re-renders don't spam the funnel.
  useEffect(() => {
    if (open) {
      track('signup_modal_opened', { default_tab: defaultTab });
    }
  }, [open, defaultTab]);

  // Update active tab when defaultTab changes
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regAccountType, setRegAccountType] = useState<'user' | 'trainer'>('user');

  // Password visibility state
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);

  const resetForms = () => {
    setLoginEmail('');
    setLoginPassword('');
    setRegName('');
    setRegEmail('');
    setRegPassword('');
    setRegConfirmPassword('');
    setForgotEmail('');
    setShowForgotPassword(false);
    setResetSent(false);
    setNewPassword('');
    setConfirmNewPassword('');
    setShowResetPassword(false);
    setResetComplete(false);
    setError(null);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetSent(true);
        toast.success('Password reset link sent to your email');
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmNewPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!isPasswordStrong(newPassword)) {
      toast.error('Password does not meet all requirements');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetComplete(true);
        toast.success('Password reset successfully!');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    track('login_submitted');
    try {
      const data = await login(loginEmail, loginPassword);
      if (data?.requires_2fa) {
        // Keep auth modal open — TwoFactorModal will render on top
        setIsLoading(false);
        return;
      }
      track('login_succeeded', { method: 'email' });
      if (data?.user?.user_id) {
        identifyUser(data.user.user_id, { email: data.user.email, account_type: data.user.account_type });
      }
      toast.success('Welcome back!');
      resetForms();
      onOpenChange(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      track('login_failed', { reason: errorMessage.slice(0, 100) });
      // iter356 — no toast here: AuthContext.login already sets `error`, which
      // its own effect toasts. Both fired, so one failed login showed the
      // "Network error" message twice.
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPasswordStrong(regPassword)) {
      toast.error('Password does not meet all requirements');
      return;
    }

    setIsLoading(true);
    track('signup_submitted', { account_type: regAccountType });
    try {
      const data = await register(regName, regEmail, regPassword, regAccountType);
      track('signup_succeeded', { account_type: regAccountType });
      const newUserId = (data as any)?.user?.user_id || (data as any)?.user_id;
      if (newUserId) {
        identifyUser(newUserId, { email: regEmail, account_type: regAccountType });
      }
      toast.success(regAccountType === 'trainer' ? 'Trainer account created! Complete your profile to appear in the public list.' : 'Account created! You have a 2-week free trial.');
      resetForms();
      onOpenChange(false);
    } catch (err) {
      // Surface the most actionable error possible. Production CDNs will
      // sometimes strip the JSON body of 400 responses (we've seen this on
      // Cloudflare for `/api/auth/register` under WAF rules), leaving us
      // with just `Request failed (400)` from the api layer. In that case
      // a vague toast frustrates users who can't see *why* sign-up failed.
      // Map common signals to a checklist that the user can verify.
      const raw = err instanceof Error ? err.message : String(err);
      let displayError = raw || 'Registration failed';
      const lower = raw.toLowerCase();

      if (raw.includes('400') && !raw.includes(':')) {
        // Body was stripped by proxy/CDN — give the user the four most
        // common reasons so they can self-diagnose without DevTools.
        displayError =
          'Sign-up failed. Please double-check: (1) the email isn\u2019t already in use, ' +
          '(2) you used a real email (no temp/disposable), ' +
          '(3) password meets all the listed requirements, ' +
          '(4) Confirm Password matches.';
      } else if (lower.includes('unable to create account') || lower.includes('already')) {
        displayError = 'An account with this email already exists. Please sign in instead.';
      } else if (lower.includes('disposable') || lower.includes('permanent email')) {
        displayError = 'Please use a permanent email address (not a temporary/disposable one).';
      } else if (lower.includes('password too weak') || lower.includes('password must')) {
        displayError = raw; // backend already itemized requirements
      } else if (lower.includes('too many accounts')) {
        displayError = raw;
      } else if (lower.includes('network') || lower.includes('failed to fetch')) {
        displayError = 'Network error. Please check your connection and try again.';
      }
      track('signup_failed', { reason: displayError.slice(0, 100) });
      toast.error(displayError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    track('google_oauth_started');
    loginWithGoogle();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) resetForms();
      onOpenChange(value);
    }}>
      <DialogContent className="bg-[#1b2028] border-[#20262f] max-w-md max-h-[85vh] p-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Authentication</DialogTitle>
          <DialogDescription>Sign in or create an account to sync your workouts</DialogDescription>
        </VisuallyHidden>
        {showResetPassword ? (
          // Reset Password Form (from email link)
          <div className="p-6 space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {resetComplete ? 'Password Reset!' : 'Create New Password'}
              </h2>
              <p className="text-[#a8abb3] text-sm mt-1">
                {resetComplete 
                  ? 'Your password has been changed successfully' 
                  : 'Enter your new password below'}
              </p>
            </div>

            {resetComplete ? (
              <div className="text-center space-y-4">
                <div className="bg-[#cafd00]/10 border border-green-500/30 rounded-lg p-4">
                  <div className="h-8 w-8 text-[#cafd00] mx-auto mb-2">✓</div>
                  <p className="text-[#cafd00] text-sm">
                    You can now sign in with your new password
                  </p>
                </div>
                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetComplete(false);
                  }}
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-[#f1f3fc]">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 bg-[#1b2028] border-[#20262f] text-white"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-3 text-[#a8abb3] hover:text-[#f1f3fc] transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordChecklist password={newPassword} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password" className="text-[#f1f3fc]">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                    <Input
                      id="confirm-new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="pl-10 bg-[#1b2028] border-[#20262f] text-white"
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={isLoading}
                  data-testid="reset-password-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            )}
          </div>
        ) : (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'login' | 'register')} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#1b2028]">
            <TabsTrigger 
              value="login" 
              data-testid="auth-login-tab"
              className="data-[state=active]:bg-[#20262f]"
            >
              Sign In
            </TabsTrigger>
            <TabsTrigger 
              value="register" 
              data-testid="auth-register-tab"
              className="data-[state=active]:bg-[#20262f]"
            >
              Sign Up
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="p-6 space-y-4">
            {showForgotPassword ? (
              // Forgot Password Form
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Reset Password</h2>
                  <p className="text-[#a8abb3] text-sm mt-1">
                    {resetSent 
                      ? 'Check your email for reset instructions' 
                      : 'Enter your email to receive a reset link'}
                  </p>
                </div>

                {resetSent ? (
                  <div className="text-center space-y-4">
                    <div className="bg-[#cafd00]/10 border border-green-500/30 rounded-lg p-4">
                      <Mail className="h-8 w-8 text-[#cafd00] mx-auto mb-2" />
                      <p className="text-[#cafd00] text-sm">
                        We have sent a password reset link to <strong>{forgotEmail}</strong>
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-[#20262f] hover:bg-[#1b2028] text-white"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetSent(false);
                        setForgotEmail('');
                      }}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-[#f1f3fc]">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="your@email.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="pl-10 bg-[#1b2028] border-[#20262f] text-white"
                          data-testid="forgot-email-input"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={isLoading}
                      data-testid="forgot-submit-btn"
                    >
                      {isLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full text-[#a8abb3] hover:text-white text-sm transition-colors"
                    >
                      ← Back to Sign In
                    </button>
                  </form>
                )}
              </div>
            ) : (
              // Regular Login Form
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Welcome Back</h2>
                  <p className="text-[#a8abb3] text-sm mt-1">Sign in to sync your workouts</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-[#f1f3fc]">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 bg-[#1b2028] border-[#20262f] text-white"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-[#f1f3fc]">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 pr-10 bg-[#1b2028] border-[#20262f] text-white"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-3 text-[#a8abb3] hover:text-[#f1f3fc] transition-colors"
                        data-testid="login-password-toggle"
                      >
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-orange-400 hover:text-orange-300 text-sm transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#20262f]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#0a0e14] px-2 text-[#a8abb3]">Or continue with</span>
                  </div>
                </div>

                <Button
                  className="w-full bg-[#ff7351] hover:bg-[#e6603f] text-white border-0 font-semibold"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <Chrome className="mr-2 h-4 w-4" />
                  Google Sign in
                </Button>
              </>
            )}
          </TabsContent>

          <TabsContent value="register" className="p-6 space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Create Account</h2>
              <p className="text-[#a8abb3] text-sm mt-1">Start your 2-week free trial</p>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              {/* User / Trainer account-type switch — same visual pattern as Nutrition DB toggles */}
              <div className="flex items-center gap-2 p-1 bg-[#1b2028] rounded-xl border border-[#20262f]" data-testid="register-account-type-switch">
                <button
                  type="button"
                  onClick={() => setRegAccountType('user')}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    regAccountType === 'user'
                      ? 'bg-[#cafd00] text-[#0a0d10] shadow'
                      : 'text-[#a8abb3] hover:text-white'
                  }`}
                  data-testid="register-account-type-user"
                >
                  I&rsquo;m a User
                </button>
                <button
                  type="button"
                  onClick={() => setRegAccountType('trainer')}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                    regAccountType === 'trainer'
                      ? 'bg-[#00f1fe] text-[#0a0d10] shadow'
                      : 'text-[#a8abb3] hover:text-white'
                  }`}
                  data-testid="register-account-type-trainer"
                >
                  I&rsquo;m a Trainer
                </button>
              </div>
              {regAccountType === 'trainer' && (
                <p className="text-xs text-[#00f1fe]/80 -mt-2">
                  Free trainer profile listed at <span className="font-mono">/trainers</span>. You&rsquo;ll add picture, age and location after signup.
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="reg-name" className="text-[#f1f3fc]">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                  <Input
                    id="reg-name"
                    type="text"
                    placeholder="Your name"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    className="pl-10 bg-[#1b2028] border-[#20262f] text-white"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-[#f1f3fc]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="your@email.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="pl-10 bg-[#1b2028] border-[#20262f] text-white"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-[#f1f3fc]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                  <Input
                    id="reg-password"
                    type={showRegPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#1b2028] border-[#20262f] text-white"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPassword(!showRegPassword)}
                    className="absolute right-3 top-3 text-[#a8abb3] hover:text-[#f1f3fc] transition-colors"
                    data-testid="register-password-toggle"
                  >
                    {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordChecklist password={regPassword} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-confirm-password" className="text-[#f1f3fc]">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#a8abb3]" />
                  <Input
                    id="reg-confirm-password"
                    type={showRegConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={regConfirmPassword}
                    onChange={(e) => setRegConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#1b2028] border-[#20262f] text-white"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                    className="absolute right-3 top-3 text-[#a8abb3] hover:text-[#f1f3fc] transition-colors"
                    data-testid="register-confirm-password-toggle"
                  >
                    {showRegConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-orange-600 hover:bg-orange-700"
                disabled={isLoading}
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[#20262f]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#0a0e14] px-2 text-[#a8abb3]">Or continue with</span>
              </div>
            </div>

            <Button
              className="w-full bg-[#ff7351] hover:bg-[#e6603f] text-white border-0 font-semibold"
              onClick={handleGoogleLogin}
              data-testid="google-register-btn"
            >
              <Chrome className="mr-2 h-4 w-4" />
              Google Sign in
            </Button>

            <p className="text-center text-xs text-[#a8abb3] mt-4">
              By creating an account, you agree to our Terms of Service
            </p>
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
